<template>
  <Card class="col12-of-12--medium-up">
    <CardHeading
      :heading="heading"
      :showCapacity="!editingVideo"
      :showButton="!editingVideo"
      :currentItems="videos.length"
      :maxItems="videoLimit"
      buttonText="Add video"
      @button-click="
        editingVideo = {
          new: true,
          data: {
            order: videos.length,
            name: '',
            url: '',
          },
        }
      "
    />
    <Callout
      v-if="videos.length === videoLimit"
      message="Video limit reached"
    />
    <div v-if="!editingVideo && videos[0]">
      <draggable
        v-model="videos"
        @end="reorder($event, sectionType)"
        v-bind="dragOptions"
        :options="{ handle: '.handle-wrapper' }"
      >
        <transition-group>
          <ProfileContentItem
            v-for="(video, i) in videos"
            :key="i + 1"
            :title="video.name"
            :description="video.url"
            :image="video.url"
            @edit-click="handleVideoEditClick(video, i)"
            @delete-click="handleDeleteItem(video)"
          />
        </transition-group>
      </draggable>
    </div>
    <CardText
      v-if="!editingVideo && !videos[0]"
      text="No videos have been added."
    />
    <CardFormWrapper v-if="editingVideo">
      <Input
        :value="editingVideo.data.name"
        @input-change="handleEdit({ section: 'editingVideo', data: $event })"
        label="Video name"
        placeholder="Video name"
        name="name"
        style="margin-bottom: 0.5rem;"
      />
      <Input
        :key="videoKey"
        :value="editingVideo.data.url"
        @input-change="handleVideoUrlEdit($event)"
        label="Video embed URL"
        placeholder="https://www.youtube.com/embed/example"
        name="url"
      />
      <div class="flex" style="justify-content: flex-end; margin-top: 2rem;">
        <Button @button-click="editingVideo = false" bType="secondary">
          Cancel
        </Button>
        <Button
          :bDisabled="!videoFormComplete"
          @button-click="
            handleDoneClick({
              item: 'editingVideo',
              section: sectionType,
            })
          "
        >
          Done
        </Button>
      </div>
    </CardFormWrapper>
  </Card>
</template>

<script>
import Vue from 'vue';
import Input from '@/components/DesignSystem/Input';
import Card from '@/components/DesignSystem/Card';
import CardHeading from '@/components/DesignSystem/CardHeading';
import Callout from '@/components/DesignSystem/Callout';
import CardFormWrapper from '@/components/DesignSystem/CardFormWrapper';
import CardText from '@/components/DesignSystem/CardText';
import ProfileContentItem from '@/components/Shared/ProfileContentItem';
import Button from '@/components/DesignSystem/Button';
import draggable from 'vuedraggable';

export default {
  components: {
    Card,
    CardHeading,
    Callout,
    CardFormWrapper,
    Input,
    Button,
    CardText,
    ProfileContentItem,
    draggable,
  },
  props: {
    namespace: String,
    heading: {
      type: String,
      default: 'Video',
    },
    videoLimit: {
      type: Number,
      default: 1,
    },
    sectionType: {
      type: String,
      default: 'media',
    },
    storeInfo: Object,
    integrationPartner: Boolean,
    vendorOPP: Boolean,
  },
  data: () => ({
    editingVideo: false,
    videoKey: 9,
  }),
  computed: {
    videos: {
      get() {
        return this.$store.getters[
          `${this.namespace}/${
            this.storeInfo ? this.storeInfo.get : 'getVideos'
          }`
        ].filter(i => !i.delete);
      },
      set(payload) {
        this.$store.commit(
          `${this.namespace}/${
            this.storeInfo ? this.storeInfo.set : 'setVideos'
          }`,
          payload
        );
      },
    },
    videoFormComplete() {
      if (this.editingVideo && this.editingVideo.data) {
        if (this.editingVideo.data.name && this.editingVideo.data.url) {
          return true;
        }
      }
      return false;
    },
    dragOptions() {
      return {
        animation: 200,
        ghostClass: 'ghost',
      };
    },
  },
  methods: {
    addVideo(payload) {
      this.$store.dispatch(
        `${this.namespace}/${this.storeInfo ? this.storeInfo.add : 'addVideo'}`,
        payload
      );
    },

    editVideo(payload) {
      this.$store.dispatch(
        `${this.namespace}/${
          this.storeInfo ? this.storeInfo.edit : 'editVideo'
        }`,
        payload
      );
    },
    reorder(event, section) {
      this[section] = this[section].map((item, i) => {
        return {
          ...item,
          order: i,
        };
      });
      this.findItemsToUpdate(event, section);
      this.$store.dispatch(
        `${this.namespace}/${
          this.storeInfo ? this.storeInfo.dirty : 'addDirtyTab'
        }`,
        {
          storeKey: section,
        }
      );
    },

    findItemsToUpdate(event, section) {
      const start = event.oldIndex;
      const end = event.newIndex;

      if (start < end) {
        for (let i = start; i <= end; i++) {
          if (this[section][i]['id']) {
            this.$set(this[section][i], 'update', true);
          }
        }
      } else {
        for (let i = end; i <= start; i++) {
          if (this[section][i]['id']) {
            this.$set(this[section][i], 'update', true);
          }
        }
      }
    },

    handleDoneClick({ item, section }) {
      if (this[item]['new']) {
        this.addVideo({ section, data: this[item]['data'] });
      } else {
        this.editVideo({ section, data: this[item]['data'] });
      }
      return (this[item] = false);
    },

    handleEdit(payload) {
      Vue.set(
        this[payload.section]['data'],
        payload.data.key,
        payload.data.value
      );
      this.$forceUpdate();
    },
    handleVideoUrlEdit(event) {
      let url = event.value;

      if (url.includes('<iframe')) {
        var regex = /<iframe.*?src="(.*?)"/;
        const embed = regex.exec(event.value)[1];
        if (embed.includes('facebook')) {
          let facebookUrl = new URL(embed);
          facebookUrl.searchParams.delete('height');
          facebookUrl.searchParams.delete('width');
          url = facebookUrl.href;
        } else {
          url = embed;
        }
      }

      if (url.includes('youtube.com/watch')) {
        const i = url.indexOf('=');
        const id = url.slice(i + 1);
        url = `https://youtube.com/embed/${id}`;
      }

      if (url.includes('youtu.be')) {
        const i = url.lastIndexOf('/');
        const id = url.slice(i + 1);
        url = `https://youtube.com/embed/${id}`;
      }

      this.handleEdit({
        section: 'editingVideo',
        data: { ...event, value: url },
      });

      if (this.videoKey) {
        this.videoKey += 1;
      }

      this.$forceUpdate();
    },
    handleVideoEditClick(payload, index) {
      this.editingVideo = { new: false, data: { ...payload } };
      if (this.videoLimit > 1) {
        this.$set(
          this.editingVideo.data,
          'order',
          payload.order ? payload.order : index
        );
      }
    },
    handleDeleteItem(video) {
      const deleteSectionType =
        this.integrationPartner || this.vendorOPP ? 'video' : this.sectionType;
      const payload = {
        item: { ...video },
        section: deleteSectionType,
        modalTitle: 'Delete video?',
        modalDescription:
          'This action cannot be undone, are you sure you want to delete this item?',
      };
      this.$emit('delete-content', payload);
    },
  },
};
</script>

<style lang="scss" scoped>
.disabled {
  cursor: not-allowed !important;
}

.fa-angle-left {
  margin-right: 5px;
}

.preview-button {
  margin-top: 1rem;
  color: #4d48e8;
  font-weight: 600;
  font-size: 0.875rem;
  display: flex;
  align-items: center;

  svg {
    margin-right: 5px;
    fill: #4d48e8;
  }
}

.horizontal-line {
  color: #dadee6;
  margin: 2rem 0;
}

.ghost {
  opacity: 0;
}

.button-type-primary {
  margin-left: 8px;
}
</style>
